<template>
    <article class="phu-config-card">
        <header class="phu-config-card__header">
            <label class="phu-config-card__name">
                <input class="phu-config-card__input" :value="phu.name" :aria-label="$t('phu_production.configuration.phu_name')" :disabled="phu.fixed_layout" @input="$emit('rename', $event.target.value)" />
                <i class="i-andy-edit i-andy-m" aria-hidden="true"></i>
            </label>
            <div class="phu-config-card__actions">
                <IconButton icon="i-andy-chevron-up" :label="$t('phu_production.configuration.move_up')" :disabled="isFirst || phu.fixed_layout" @click="$emit('move', -1)" />
                <IconButton icon="i-andy-chevron-down" :label="$t('phu_production.configuration.move_down')" :disabled="isLast || phu.fixed_layout" @click="$emit('move', 1)" />
                <span class="phu-config-card__divider" aria-hidden="true"></span>
                <IconButton icon="i-andy-delete" :label="$t('phu_production.configuration.delete')" :disabled="phu.fixed_layout" @click="$emit('remove')" />
            </div>
        </header>

        <div class="phu-config-card__body">
            <p v-if="phu.fixed_layout" class="phu-config-card__fixed">{{ $t('phu_production.configuration.fixed_layout') }}</p>
            <p v-for="warning in warnings" :key="warning.productId" class="phu-config-card__warning">
                <i class="i-andy-exclamation i-andy-s" aria-hidden="true"></i>
                <span>{{ $t('phu_production.configuration.min_slots', { product: warning.name, min: minSlots, count: warning.count }) }}</span>
            </p>

            <div v-if="phu.choosing_layout" class="phu-config-card__layouts" :style="layoutGridStyle">
                <button v-for="layout in layouts" :key="layout.id" type="button" class="phu-config-card__layout" @click="$emit('select-layout', layout)">
                    {{ layout.name }}
                </button>
            </div>

            <template v-else>
                <!-- <button v-if="!phu.fixed_layout" type="button" class="phu-config-card__change" @click="$emit('edit-layout')">
                    {{ $t('phu_production.configuration.select_layout') }}
                </button> -->
                <div class="phu-config-card__grid" :style="slotGridStyle">
                    <ConfigSlot
                        v-for="slot in sortedSlots"
                        :key="slot.id"
                        :product="products[slot.product_id]"
                        :invalid="isInvalid(slot)"
                        :reason="slotReason(slot)"
                        @select="$emit('select-slot', slot)"
                        @clear="$emit('clear-slot', slot)"
                    />
                </div>
            </template>
        </div>
    </article>
</template>

<script>
import IconButton from '@/components/domain/ui/IconButton.vue'
import ConfigSlot from './ConfigSlot.vue'

const SLOT_WIDTH = 120

export default {
    name: 'PhuConfigCard',
    components: { IconButton, ConfigSlot },
    props: {
        phu: { type: Object, required: true },
        products: { type: Object, required: true },
        underAssigned: { type: Object, default: () => ({}) },
        minSlots: { type: Number, required: true },
        layouts: { type: Array, default: () => [] },
        isFirst: { type: Boolean, default: false },
        isLast: { type: Boolean, default: false }
    },
    computed: {
        layoutGridStyle() {
            return { gridTemplateColumns: `repeat(${this.layouts.length}, minmax(0, 1fr))` }
        },
        slotGridStyle() {
            if (this.$mq === 'portrait') {
                return { gridTemplateColumns: `repeat(auto-fill, minmax(${SLOT_WIDTH}px, 1fr))` }
            }
            const columns = Number(this.phu.columns) || 1
            const rows = Number(this.phu.rows) || 1
            return {
                gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                gridTemplateRows: `repeat(${rows}, minmax(70px, auto))`
            }
        },
        sortedSlots() {
            const total = (Number(this.phu.rows) || 0) * (Number(this.phu.columns) || 0)
            return [...this.phu.slots]
                .filter((slot) => slot.position >= 1 && slot.position <= total)
                .sort((a, b) => a.position - b.position)
        },
        warnings() {
            const present = {}
            this.phu.slots.forEach((slot) => {
                const count = this.assignedCount(slot)
                const product = this.products[slot.product_id]
                if (!count || !product) {
                    return
                }
                present[slot.product_id] = { name: product.name.toUpperCase(), count: count }
            })
            return Object.keys(present).map((productId) => ({ productId: productId, name: present[productId].name, count: present[productId].count }))
        }
    },
    methods: {
        assignedCount(slot) {
            if (!slot || !slot.product_id) {
                return 0
            }
            return Number(this.underAssigned[String(slot.product_id)]) || 0
        },
        isInvalid(slot) {
            return this.assignedCount(slot) > 0
        },
        slotReason(slot) {
            if (!this.isInvalid(slot)) {
                return ''
            }
            const product = this.products[slot.product_id]
            return this.$t('phu_production.configuration.min_slots', {
                product: product ? product.name : '',
                min: this.minSlots,
                count: this.assignedCount(slot)
            })
        }
    }
}
</script>

<style lang="scss" scoped>
@import '~@/components/domain/phu-production/styles/tokens';

.phu-config-card {
    @include display-flex();
    @include flex-direction(column);
    min-width: 0;
    border: 1px solid $neutro-dark;
    border-radius: $phu-radius-s;
    background-color: $phu-surface;
    box-shadow: 0 0 4px rgba(0, 0, 0, 0.04), 0 4px 8px rgba(0, 0, 0, 0.06);
    overflow: hidden;

    &__header {
        @include display-flex();
        @include align-items(center);
        gap: 24px;
        min-width: 0;
        padding: 8px 8px 4px;
    }

    &__body {
        @include display-flex();
        @include flex-direction(column);
        gap: 8px;
        padding: 4px 8px 8px;
    }

    &__layouts {
        display: grid;
        gap: 8px;
    }

    &__layout {
        @include display-flex();
        @include align-items(center);
        @include justify-content(center);
        min-height: 120px;
        padding: 4px;
        border: 1px solid $neutro-s30;
        border-radius: $phu-radius-s;
        background-color: $phu-surface;
        font-family: $text-bold;
        font-size: 1rem;
        line-height: 20px;
        color: $neutro-s50;
        cursor: pointer;
    }

    &__change {
        align-self: flex-start;
        padding: 0;
        border: none;
        background: transparent;
        font-family: $text-bold;
        font-size: 0.875rem;
        line-height: 18px;
        color: $phu-primary;
        cursor: pointer;
    }

    &__name {
        @include display-flex();
        @include align-items(center);
        flex: 1;
        gap: 16px;
        min-width: 0;
        padding: 8px;
        border: 1px solid $neutro-dark;
        border-radius: $phu-radius-s;
        color: $phu-text-muted;
    }

    &__input {
        flex: 1;
        min-width: 0;
        border: none;
        background: transparent;
        font-family: $text-bold;
        font-size: 1rem;
        line-height: 18px;
        color: $phu-text;

        &:disabled {
            color: $phu-text-muted;
        }
    }

    &__actions {
        @include display-flex();
        @include align-items(center);
        flex-shrink: 0;
        gap: 8px;
    }

    &__divider {
        width: 1px;
        height: 40px;
        background-color: $neutro-dark;
    }

    &__warning,
    &__fixed {
        @include display-flex();
        @include align-items(center);
        gap: 4px;
        margin: 4px 0;
        font-family: $text;
        font-size: 0.875rem;
        i{
            color: $phu-error;
        }
        span{
            color: $phu-error;
        }
    }

    &__grid {
        display: grid;
        gap: 8px;
    }
}
</style>
